import {
  EXTERNAL_OPEN_TASK_STATUSES,
  EXTERNAL_TODO_TASK_STATUSES,
  TaskInstance,
  TaskListType,
  TRemainingTasks,
} from '../types';
import {
  EXTERNAL_TODO_TASK_TYPES,
  REQUESTED_OR_CONDITIONALLY_REQUIRED_DOCUMENT_TASKS,
} from '../constants/task.constants';

/**
 * ERM-12595 — whether a task is a customer to-do: by type and audience, never
 * by thread (a to-do carries none until someone messages on it).
 *
 * `record_only` is excluded alongside `cancelled`: it is the ERM-13228
 * answer-change activity host, which in practice is always internal (so
 * `!task.is_internal` alone would already exclude it), but named explicitly
 * here too rather than leant on `is_completed` alone, since this predicate is
 * exercised directly against hand-built tasks (tests) that may not carry it.
 */
export const isExternalToDoTask = (task: TaskInstance): boolean =>
  EXTERNAL_TODO_TASK_TYPES.has(task.task_type) &&
  !task.is_internal &&
  task.status !== 'cancelled' &&
  task.status !== 'record_only';

/**
 * ERM-12595 — whether a to-do is done, decided by the task, never by its thread.
 *
 * The thread is not a second signal: the #5910 auto-resolve closes the task's
 * thread and moves the status together, and the one backend path that resolves
 * a thread on its own (complete-qc-answers, for a question that stopped meeting
 * its conditions) resolves the *answer* thread, which is not the one that
 * arrives as `task.thread`. Reading it here would only let a reply on a
 * satisfied to-do reopen it, since every external message unresolves a thread.
 *
 * `is_completed` is kept as the explicit signal even though the backend derives
 * it from a terminal status, so a future terminal status is covered too.
 */
export const isExternalToDoDone = (task: TaskInstance): boolean =>
  task.is_completed ||
  task.status === 'ready_for_review' ||
  task.status === 'review_completed';

/**
 * ERM-12595 — an open customer to-do: what every section-level count counts,
 * consistent with the Collab Hub list under PR #6445.
 */
export const isOpenExternalToDo = (task: TaskInstance): boolean =>
  isExternalToDoTask(task) && !isExternalToDoDone(task);

/**
 * Whether a to-do is still open for the external user, decided by status alone.
 *
 * ERM-12647: the thread is no longer the signal. The documents-as-a-whole
 * to-do (ERM-12395) is workspace-scoped and carries no thread, and conditional
 * question/section to-dos arrive threadless too, so a thread-based filter
 * under-counts an open correction cycle — and the banner it gates holds the
 * only Resubmit button, so Submit becomes unreachable.
 *
 * Now that `isExternalToDoDone` reads the task rather than the thread, this and
 * `isOpenExternalToDo` agree on every task the remaining-to-do fetch returns:
 * the fetch already asks for non-internal to-do types only, and the statuses
 * left over are exactly EXTERNAL_OPEN_TASK_STATUSES. This one stays because
 * Submit reads it and cares only about status.
 */
export function isOpenExternalToDoTask(task: TaskInstance): boolean {
  return EXTERNAL_OPEN_TASK_STATUSES.includes(task.status);
}

/**
 * Question ids whose to-do is still open for the external user (ERM-13220).
 *
 * Openness is the task's status, not its thread's: resolving a thread does not
 * close the to-do, so a thread-based check would let a second one be created
 * mid correction cycle.
 */
export function openExternalToDoQuestionIds(
  questionToDoTasks: ReadonlyMap<string, TaskInstance>,
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const [questionId, task] of questionToDoTasks) {
    if (isOpenExternalToDoTask(task)) {
      ids.add(questionId);
    }
  }
  return ids;
}

/**
 * Narrows categorized remaining tasks down to the ones still open for the
 * external user, keeping the buckets intact. The set Submit hands back for
 * review, so it stays on status alone.
 */
export function filterOpenExternalToDoTasks(
  tasks?: TRemainingTasks | null,
): TRemainingTasks {
  return {
    document: tasks?.document?.filter(isOpenExternalToDoTask) ?? [],
    other: tasks?.other?.filter(isOpenExternalToDoTask) ?? [],
    question: tasks?.question?.filter(isOpenExternalToDoTask) ?? [],
    section: tasks?.section?.filter(isOpenExternalToDoTask) ?? [],
  };
}

/**
 * Maps a task to the task-list bucket its status updates belong to.
 * Anything that is not a question or a section is tracked as a document.
 */
export function toTaskListType(task: TaskInstance): TaskListType {
  if (task.task_type === 'question' || task.task_type === 'section') {
    return task.task_type;
  }
  return 'document';
}

/**
 * Determines whether a question task should appear in external-only to-do maps:
 * an open customer to-do (ERM-12595) — threadless ones count, done ones drop.
 * The redesign (collab_hub_redesign on) semantics.
 */
export const isExternalRemainingQuestionToDoTask = (
  task: TaskInstance,
): boolean => !!task.task_entity?.question_id && isOpenExternalToDo(task);

/**
 * Flag-off variant of isExternalRemainingQuestionToDoTask: the legacy v1 hub
 * keeps any threaded task — resolved or not — plus threadless open statuses.
 */
export const isLegacyExternalRemainingQuestionToDoTask = (
  task: TaskInstance,
): boolean => {
  if (!task.task_entity?.question_id) {
    return false;
  }

  return !!task.thread || EXTERNAL_TODO_TASK_STATUSES.includes(task.status);
};

/**
 * Builds a question-id keyed map of remaining external question to-do tasks.
 * The store passes the legacy predicate while collab_hub_redesign is off.
 */
export function buildExternalRemainingQuestionToDoTasksMap(
  tasks: TaskInstance[],
  isRemaining: (
    task: TaskInstance,
  ) => boolean = isExternalRemainingQuestionToDoTask,
): Map<string, TaskInstance> {
  if (!tasks?.length) {
    return new Map<string, TaskInstance>();
  }

  return new Map<string, TaskInstance>(
    tasks
      .filter(isRemaining)
      .map((task) => [task.task_entity!.question_id!, task]),
  );
}

/**
 * Determines whether a section task should appear in external-only to-do maps:
 * an open customer to-do (ERM-12595) — threadless ones count, done ones drop.
 * The redesign (collab_hub_redesign on) semantics.
 */
export const isExternalRemainingSectionToDoTask = (
  task: TaskInstance,
): boolean => !!task.task_entity?.section_id && isOpenExternalToDo(task);

/**
 * Flag-off variant of isExternalRemainingSectionToDoTask: the legacy v1 hub
 * keeps any threaded task — resolved or not — plus threadless open statuses.
 */
export const isLegacyExternalRemainingSectionToDoTask = (
  task: TaskInstance,
): boolean => {
  if (!task.task_entity?.section_id) {
    return false;
  }

  return !!task.thread || EXTERNAL_TODO_TASK_STATUSES.includes(task.status);
};

/**
 * Builds a section-id keyed map of remaining external section to-do tasks.
 * The store passes the legacy predicate while collab_hub_redesign is off.
 */
export function buildExternalRemainingSectionToDoTasksMap(
  tasks: TaskInstance[],
  isRemaining: (
    task: TaskInstance,
  ) => boolean = isExternalRemainingSectionToDoTask,
): Map<string, TaskInstance> {
  if (!tasks?.length) {
    return new Map<string, TaskInstance>();
  }

  return new Map<string, TaskInstance>(
    tasks
      .filter(isRemaining)
      .map((task) => [task.task_entity!.section_id!, task]),
  );
}

const initialRemainingTasks: TRemainingTasks = {
  document: [] as TaskInstance[],
  question: [] as TaskInstance[],
  section: [] as TaskInstance[],
  other: [] as TaskInstance[],
};

/*
 * Categorizes remaining internal and incomplete tasks into document, section, and question types.
 *
 * @param response - Array of TaskInstance objects to categorize
 * @returns TRemainingTasks object with tasks categorized by their entity type
 */
export function categorizeRemainingTasks(
  response?: TaskInstance[],
): TRemainingTasks {
  // Early return for empty input
  if (!response || response.length === 0) {
    return { ...initialRemainingTasks };
  }

  // Initialize result object
  const result: TRemainingTasks = {
    document: [],
    other: [],
    question: [],
    section: [],
  };

  // Single pass through the array for optimal performance
  for (const task of response) {
    // Skip non-internal or completed tasks

    const taskEntityType = task.task_entity?.type;

    if (taskEntityType) {
      switch (taskEntityType) {
        case 'document':
          result.document.push(task);
          break;
        case 'section':
          result.section.push(task);
          break;
        case 'question':
          result.question.push(task);
          break;
        case 'condition':
          if (task.task_type === 'document_conditionally_required') {
            result.document.push(task);
          } else {
            result.other.push(task);
          }
          break;
        default:
          result.other.push(task);
          break;
      }
    } else {
      // Document task types without an entity still belong in the document bucket.
      if (
        REQUESTED_OR_CONDITIONALLY_REQUIRED_DOCUMENT_TASKS.has(task.task_type)
      ) {
        result.document.push(task);
      }
    }
  }

  return result;
}
